import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCGrid, RCCHookToast, RCCRefLovProps, RCCServicesApi } from "react-common-components";
import Tabs from "src/components/Tabs";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import SortableContext from "src/components/Sortable/Context";
import LovDimensions from "src/components/Lov/Dimensions";
import { useForm, useWatch } from "react-hook-form";
import DragOverlay from "src/components/Sortable/DragOverlay";
import { standardErrorMessages } from "src/utils/errors";
import { css } from "styled-components";
import stringToNumber from "src/utils/stringToNumber";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { simplePermissionType } from "src/components/Permissions/interface";
import { ISwot, ISwotItem, ISwotMatrix, SWOTType } from "../../interface";
import MatrixItem from "./itemMatrix";
import { ExternalTitleWrapper, ExternalContainerTitle, ExternalTitle, Wrapper, MatrixContainer } from "./styles";
import CardSwotMatrix from "../SWOTCard";
import SWOTItens from "../SWOTItens";
import SWOTStrategies from "../SWOTStrategies";
import { IRefSwotStrategiesTab } from "../SWOTStrategies/interface";
import swotLabels from "../../SwotLabels";

const MatrixSWOT: React.FC<ISwotMatrix> = ({
	swotId,
	swotTitle,
	filters
}) => {

	const { translate } = useTranslation();
	const { addToast} = RCCHookToast();
	const [container1, setContainer1] = useState<ISwotItem[]>([]);
	const [container2, setContainer2] = useState<ISwotItem[]>([]);
	const [container3, setContainer3] = useState<ISwotItem[]>([]);
	const [container4, setContainer4] = useState<ISwotItem[]>([]);
	const [allItens,setAllItens] = useState<ISwotItem[]>([]);
	const [activeCard,setActiveCard] = useState<ISwotItem>();
	const [selectedSwot, setSelectedSwot] = useState<ISwot>({} as ISwot);
	const [tabStrategiesActive, setTabStrategiesActive] = useState(false);

	const { setLoading } = useLoaderBackdrop();
	const {swotSections} = swotLabels(translate);	

	const lovRef = useRef<RCCRefLovProps>(null);
	const tabRef = useRef<IRefSwotStrategiesTab>(null);
	const {
		control,
		setValue
	}= useForm();

	const dimensionWatch = useWatch({
		control,
		name: "mes001Taa011Id"
	});

	const requestSwotItens = async(mes001Id:number,taa011Id:number) => {
		try {
			setLoading(true);
			const dataController = await RCCServicesApi.getRequest({
				method: "SWOT/ListSWOTItens",
				urlParameters: {
					mes001Id,taa011Id
				}
			});
			const dataItens = dataController.data as ISwotItem[];
			setAllItens(dataItens);
			setContainer1(() => dataItens.filter((x) => x.mes002Secao === SWOTType.STRENGTHS));
			setContainer2(() => dataItens.filter((x) => x.mes002Secao === SWOTType.WEAKNESSES));
			setContainer3(() => dataItens.filter((x) => x.mes002Secao === SWOTType.OPPORTUNITIES));
			setContainer4(() => dataItens.filter((x) => x.mes002Secao === SWOTType.THREATS));
			setLoading(false);
		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar itens da análise"), addToast);
			setLoading(false);
		}
	};

	const requestSwot = async(id:number) => {
		const dataController = await RCCServicesApi.getRequest({
			method: "SWOT/Select",
			urlParameters: {
				id
			}
		});
		
		lovRef.current?.setValue(dataController.data?.mes001Taa011Id?.toString());
		setValue("mes001Taa011Id",dataController.data?.mes001Taa011Id?.toString());
		setSelectedSwot(dataController.data);
	};

	function findContainer(id: number | string):number {
		let idConverted = id;
		if(typeof id === "string")
			idConverted = parseInt(id, 10);	
		const selectedItem = allItens.find((item) => item.mes002Id === idConverted);
		return selectedItem?.mes002Secao ?? -1;
	}

	const returnSwotProp = (prop:"title"|"color",swotSection:SWOTType) => {
		if(prop === "title")
			return swotSections.find((x) => x.type===swotSection)?.title;
		return swotSections.find((x) => x.type===swotSection)?.color;
	};

	function returnContainer(nameOfContainer: SWOTType):{itens:ISwotItem[],set: (itens: ISwotItem[]) => void;}{

		switch (nameOfContainer) {
		case SWOTType.STRENGTHS:
			return {itens:[...container1], set: (itens: ISwotItem[]) => setContainer1(itens)};
		case SWOTType.WEAKNESSES:
			return {itens:[...container2], set: (itens: ISwotItem[]) => setContainer2(itens)};
		case SWOTType.OPPORTUNITIES:
			return {itens:[...container3], set: (itens: ISwotItem[]) => setContainer3(itens)};
		default:
			return {itens:[...container4], set: (itens: ISwotItem[]) => setContainer4(itens)};
		}
	}

	const handleDragEnd = async(activeId: string, overId: string) => {
		if(canChange()){
			const activeContainer = findContainer(activeId);
			const activeElement = {...allItens.find((item) => item.id === activeId) as ISwotItem};
			const containerId = findContainer(overId);
			if(!activeElement || (!activeContainer && activeContainer !== 0))
				return;

			const overContainer = containerId !== -1 ? containerId : parseInt(overId,10);
			const activeItems = returnContainer(activeContainer);
			const overItems = returnContainer(overContainer);
			const prevAllItens = [...allItens];

			if(activeContainer !== overContainer){
				try {
					activeElement.mes002Secao = overContainer;
					const newActiveItens = activeItems.itens.filter((swotItem: ISwotItem) => swotItem.id !== activeId);
					const newOverItems = [...overItems.itens,activeElement];
					
					activeItems.set(newActiveItens);
					overItems.set(newOverItems.sort((a, b) => b.mes002Pontuacao - a.mes002Pontuacao));
					
					setAllItens((prevState) => 
						prevState.map((x) => {
							if(x.mes002Id === activeElement.mes002Id)
								return {...x,mes002Secao:overContainer};
							return x;
						})
					);
					
					await RCCServicesApi.putRequest({
						method: "SWOT/UpdateSwotType",
						urlParameters: {
							swotItemId:parseInt(activeId, 10),
							type:overContainer
						}
					});
					
				} catch (error) {
					standardErrorMessages(error, translate("Erro ao atualizar item"), addToast);
					activeItems.set(activeItems.itens);
					overItems.set(overItems.itens);
					setAllItens(prevAllItens);
				}
			}
			
			setActiveCard(undefined);
		}
	};

	const handleDragStart = (activeId: string) => {
		if(canChange()){
			const element = allItens.find((elem) => elem.id === activeId);
			if(!element) return;
			setActiveCard(element);	
		 }
	};
	
	const canChange = () => 
		dimensionWatch && 
		dimensionWatch !== "0" && 
		selectedSwot?.userPermission?.toString() === simplePermissionType.VIEW_AND_EDIT;

	useEffect(() => {
		if(swotId){
			requestSwot(swotId);
		}else{
			setSelectedSwot({} as ISwot);
		}

	}, [swotId]);  

	useEffect(() => {
		if (stringToNumber(dimensionWatch) < 0){
			lovRef.current?.clearValue();
		}else if(dimensionWatch && selectedSwot.mes001Id){
			requestSwotItens(selectedSwot.mes001Id,dimensionWatch);
		}
		
	}, [selectedSwot.mes001Id,dimensionWatch]);

	useEffect(() => {
		if(filters?.map !== "" || filters?.perspective !== "" || filters?.objective !== ""){
			setTabStrategiesActive(true);
		}else{
			setTabStrategiesActive(false);
		}
	},[filters]);

	return (
		<>
			{selectedSwot?.mes001Id ? 
				<>
					<Title color="purple" size="xl">
						{selectedSwot.mes001Titulo}
					</Title>
					<RCCGrid container item sm={8} md={4}>
						<LovDimensions 
							name="mes001Taa011Id" 
							label={translate("Dimensão padrão")} 
							defaultValue={selectedSwot?.mes001Taa011Id?.toString()}
							required
							control={control}
							ref={lovRef}
						/>
					</RCCGrid>
					<Tabs
						name="swotMatrixTab"
						tabInit={tabStrategiesActive ? 5 : 0}
						styleInline={css`
							@media (min-width: 1000px){
								&.MuiTab-root .MuiButtonBase-root {
									min-width: 160px;
								}
							}
							@media (min-width: 600px){
								&.MuiTab-root .MuiButtonBase-root {
									min-width: 120px;
								}
							}`}
						options={[
							{
								label: translate("Matriz"),
								onClick:() => {requestSwotItens(selectedSwot.mes001Id,parseInt(dimensionWatch,10));},
								content:   
                                <MatrixContainer>
                                	<ExternalTitleWrapper className="horizontal">
                                		<ExternalContainerTitle>
                                			<ExternalTitle>
                                				<span>{translate("Ajuda a realização dos objetivos")}</span>
                                			</ExternalTitle> 
                                		</ExternalContainerTitle>
                                		<ExternalContainerTitle >
                                			<ExternalTitle>
                                				<span>{translate("Atrapalha a realização dos objetivos")}</span>
                                			</ExternalTitle> 
                                		</ExternalContainerTitle>
                                	</ExternalTitleWrapper>

                                	<ExternalTitleWrapper className="vertical">
                                		<ExternalContainerTitle>
                                			<ExternalTitle>
                                				<span>{translate("Origem interna")}</span>
                                			</ExternalTitle>
                                		</ExternalContainerTitle>
                                		<ExternalContainerTitle>
                                			<ExternalTitle>
                                				<span>{translate("Origem externa")}</span>
                                			</ExternalTitle>
                                		</ExternalContainerTitle>
                                	</ExternalTitleWrapper>
                                	<SortableContext 
                                		onDragEnd={(activeId, overId) => handleDragEnd(activeId, overId)}
                                		onDragStart={(activeId) => handleDragStart(activeId)}
                                	>
                                	<Wrapper>
                                		{/* Forças */}
                                		<MatrixItem 
                                			number={SWOTType.STRENGTHS.toString()}
                                			color={returnSwotProp("color",SWOTType.STRENGTHS)}
                                			data={container1} 
                                			titles={{
                                				heading: `${translate("Quais os pontos fortes de seu negócio")}?`,
                                				title1: returnSwotProp("title",SWOTType.STRENGTHS)
                                			}}
                                		/>

                                		{/* Fraquezas */}
                                		<MatrixItem 
                                			number={SWOTType.WEAKNESSES.toString()}
                                			color={returnSwotProp("color",SWOTType.WEAKNESSES)}
                                			data={container2} 
                                			titles={{
                                				heading: `${translate("Quais os pontos fracos de seu negócio")}?`,
                                				title1: returnSwotProp("title",SWOTType.WEAKNESSES)
                                			}}
                                		/>
                        
                                		{/* Oportunidades */}
                                		<MatrixItem 
                                			number={SWOTType.OPPORTUNITIES.toString()} 
                                			color={returnSwotProp("color",SWOTType.OPPORTUNITIES)}
                                			data={container3} 
                                			titles={{
                                				heading: `${translate("Quais as oportunidades para seu negócio")}?`,
                                				title1: returnSwotProp("title",SWOTType.OPPORTUNITIES)
                                			}}
                                		/>

                                		{/* Ameaças */}
                                		<MatrixItem 
                                			number={SWOTType.THREATS.toString()}
                                			color={returnSwotProp("color",SWOTType.THREATS)}
                                			data={container4} 
                                			titles={{
                                				heading: `${translate("Quais as ameaças para seu negócio")}?`,
                                				title1: returnSwotProp("title",SWOTType.THREATS)
                                			}}
                                		/>
                                	</Wrapper>
                                		<div style={{ listStyle: "none" }}>
                                			<DragOverlay>
                                				{activeCard ? (
                                					<CardSwotMatrix data={activeCard}/>
                                				): null}
                                			</DragOverlay>
                                		</div>
                                		
                                	</SortableContext>
                                </MatrixContainer>
							},
							{
								label: returnSwotProp("title",SWOTType.STRENGTHS),
								content:   
                    			<SWOTItens
                    				data={container1}
                    				type={SWOTType.STRENGTHS}
                    				canChange={canChange()}
                    				SWOTId={swotId}
                    				swotTitle={swotTitle}
                    				dimensionId={parseInt(dimensionWatch,10)}
                    			/>
							},
							{
								label: returnSwotProp("title",SWOTType.WEAKNESSES),
								content:   
                    			<SWOTItens
                    				data={container2}
                    				type={SWOTType.WEAKNESSES}
                    				canChange={canChange()}
                    				SWOTId={swotId}
                    				swotTitle={swotTitle}
                    				dimensionId={parseInt(dimensionWatch,10)}
                    			/>
							},
							{
								label: returnSwotProp("title",SWOTType.OPPORTUNITIES),
								content:   
                    			<SWOTItens
                    				data={container3}
                    				type={SWOTType.OPPORTUNITIES}
                    				canChange={canChange()}
                    				SWOTId={swotId}
                    				swotTitle={swotTitle}
                    				dimensionId={parseInt(dimensionWatch,10)}
                    			/>
							},
							{
								label: returnSwotProp("title",SWOTType.THREATS),
								content:   
                    			<SWOTItens
                    				data={container4}
                    				type={SWOTType.THREATS}
                    				canChange={canChange()}
                    				SWOTId={swotId}
                    				swotTitle={swotTitle}
                    				dimensionId={parseInt(dimensionWatch,10)}
                    			/>
							},
							{
								label: translate("Estratégias"),
								onClick:() => tabRef.current?.reQueryObjectives(),
								content:
									<SWOTStrategies 
										swotId={swotId}
										filters={filters}
										ref={tabRef}
										canChange={canChange()}
									/>
							}
						]}
					/>
				</>
				:
				<RCCAlert
					title={translate("Nenhuma análise SWOT selecionada")}
					description={translate("Selecione uma análise para ver seus detalhes")}
				/>
			}
			
		</>
	);
};

export default MatrixSWOT;
